import React, { useEffect, useState } from 'react';
import { Button, Card, Icon, Label, Table, Popup } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';

const SubscriptionPlan = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/subscription/plan/all');
      const { success, data, message } = res.data;
      if (success) {
        setPlans(data || []);
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const deletePlan = async (id, code) => {
    if (!window.confirm(t('subscription_plan.delete_confirm', { name: code }))) return;
    try {
      const res = await API.delete(`/api/subscription/plan/${id}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('subscription_plan.deleted'));
        loadPlans();
      } else {
        showError(message);
      }
    } catch (e) {
      showError(e.message);
    }
  };

  const renderModels = (modelsJSON) => {
    if (!modelsJSON || modelsJSON === '[]') {
      return <Label color='orange'>{t('subscription_plan.unlimited_models')}</Label>;
    }
    try {
      const models = JSON.parse(modelsJSON);
      if (!Array.isArray(models) || models.length === 0) {
        return <Label color='orange'>{t('subscription_plan.unlimited_models')}</Label>;
      }
      return (
        <Label.Group>
          {models.map((m) => (
            <Label key={m} size='small'>{m}</Label>
          ))}
        </Label.Group>
      );
    } catch {
      return <Label color='red'>{t('subscription_plan.invalid_models')}</Label>;
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {t('subscription_plan.title')}
            <Button
              as={Link}
              to='/subscription-plan/add'
              floated='right'
              color='green'
              size='small'
            >
              <Icon name='plus' /> {t('subscription_plan.add')}
            </Button>
          </Card.Header>
          <Table celled striped compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t('subscription_plan.code')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.name')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.level')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.price')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.period_days')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.monthly_quota')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.models')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.status')}</Table.HeaderCell>
                <Table.HeaderCell>{t('subscription_plan.actions')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {plans.map((plan) => (
                <Table.Row key={plan.id}>
                  <Table.Cell>{plan.code}</Table.Cell>
                  <Table.Cell>{plan.name}</Table.Cell>
                  <Table.Cell>
                    <Label color={plan.level === 'free' ? 'grey' : plan.level === 'ultra' ? 'purple' : 'blue'}>
                      {plan.level}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>¥{(plan.price_cents / 100).toFixed(2)}</Table.Cell>
                  <Table.Cell>{plan.period_days}</Table.Cell>
                  <Table.Cell>{plan.monthly_quota === 0 ? t('subscription_plan.unlimited') : plan.monthly_quota.toLocaleString()}</Table.Cell>
                  <Table.Cell>{renderModels(plan.models)}</Table.Cell>
                  <Table.Cell>
                    {plan.status === 1 ? (
                      <Label color='green'>{t('subscription_plan.online')}</Label>
                    ) : (
                      <Label color='grey'>{t('subscription_plan.offline')}</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      as={Link}
                      to={`/subscription-plan/edit/${plan.id}`}
                      size='mini'
                      color='blue'
                    >
                      <Icon name='edit' />
                    </Button>
                    {plan.code !== 'internal_unlimited' && (
                      <Popup
                        content={t('subscription_plan.delete')}
                        trigger={
                          <Button
                            size='mini'
                            color='red'
                            onClick={() => deletePlan(plan.id, plan.code)}
                          >
                            <Icon name='trash' />
                          </Button>
                        }
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Card.Content>
      </Card>
    </div>
  );
};

export default SubscriptionPlan;
